import type { StatusBadgeTone } from 'shared/utils/status-tone.util';

export const CANTEEN_TENANT_ID = 'tenant-oneeduca';
export const CANTEEN_SCHOOL_ID = 'school-centro';
export const CANTEEN_ID = 'canteen-centro';

export const CANTEEN_ROLES = [
  'CANTEEN_ADMIN',
  'CANTEEN_MANAGER',
  'CANTEEN_OPERATOR',
  'GUARDIAN',
  'STUDENT',
] as const;

export type CanteenRole = (typeof CANTEEN_ROLES)[number];

export const CANTEEN_ROLE_LABELS: Record<CanteenRole, string> = {
  CANTEEN_ADMIN: 'Administrador',
  CANTEEN_MANAGER: 'Gestor da cantina',
  CANTEEN_OPERATOR: 'Operador',
  GUARDIAN: 'Responsável',
  STUDENT: 'Aluno',
};

export const CANTEEN_WEEKDAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

export type CanteenWeekday = (typeof CANTEEN_WEEKDAYS)[number];

export const CANTEEN_WEEKDAY_LABELS: Record<CanteenWeekday, string> = {
  monday: 'Segunda-feira',
  tuesday: 'Terça-feira',
  wednesday: 'Quarta-feira',
  thursday: 'Quinta-feira',
  friday: 'Sexta-feira',
  saturday: 'Sábado',
  sunday: 'Domingo',
};

export const CANTEEN_UNIT_STATUSES = [
  'ACTIVE',
  'INACTIVE',
  'SUSPENDED',
] as const;
export type CanteenUnitStatus = (typeof CANTEEN_UNIT_STATUSES)[number];

export const CANTEEN_UNIT_STATUS_LABELS: Record<CanteenUnitStatus, string> = {
  ACTIVE: 'Ativa',
  INACTIVE: 'Inativa',
  SUSPENDED: 'Suspensa',
};

export const CANTEEN_ACCOUNT_STATUSES = [
  'ACTIVE',
  'BLOCKED',
  'SUSPENDED',
] as const;
export type CanteenAccountStatus = (typeof CANTEEN_ACCOUNT_STATUSES)[number];

export const CANTEEN_ACCOUNT_STATUS_LABELS: Record<
  CanteenAccountStatus,
  string
> = {
  ACTIVE: 'Ativa',
  BLOCKED: 'Bloqueada',
  SUSPENDED: 'Suspensa',
};

export const CANTEEN_STUDENT_STATUSES = ['ACTIVE', 'INACTIVE'] as const;
export type CanteenStudentStatus = (typeof CANTEEN_STUDENT_STATUSES)[number];

export const CANTEEN_ORDER_STATUSES = [
  'CREATED',
  'PENDING_PAYMENT',
  'PAID',
  'SCHEDULED',
  'PREPARING',
  'READY',
  'READY_FOR_PICKUP',
  'DELIVERED',
  'CANCELLED',
  'REFUNDED',
] as const;

export type CanteenOrderStatus = (typeof CANTEEN_ORDER_STATUSES)[number];

export const CANTEEN_ORDER_STATUS_LABELS: Record<CanteenOrderStatus, string> = {
  CREATED: 'Criado',
  PENDING_PAYMENT: 'Pagamento pendente',
  PAID: 'Pago',
  SCHEDULED: 'Agendado',
  PREPARING: 'Em preparo',
  READY: 'Pronto',
  READY_FOR_PICKUP: 'Pronto para retirada',
  DELIVERED: 'Entregue',
  CANCELLED: 'Cancelado',
  REFUNDED: 'Pagamento devolvido',
};

export const CANTEEN_ORDER_STATUS_TONES: Record<
  CanteenOrderStatus,
  StatusBadgeTone
> = {
  CREATED: 'neutral',
  PENDING_PAYMENT: 'warning',
  PAID: 'info',
  SCHEDULED: 'info',
  PREPARING: 'warning',
  READY: 'success',
  READY_FOR_PICKUP: 'success',
  DELIVERED: 'neutral',
  CANCELLED: 'danger',
  REFUNDED: 'danger',
};

export const CANTEEN_PAYMENT_METHODS = [
  'BALANCE',
  'CASH',
  'PIX',
  'CARD',
  'OTHER',
] as const;

export type CanteenPaymentMethod = (typeof CANTEEN_PAYMENT_METHODS)[number];

export const CANTEEN_PAYMENT_LABELS: Record<CanteenPaymentMethod, string> = {
  BALANCE: 'Saldo',
  CASH: 'Dinheiro',
  PIX: 'PIX',
  CARD: 'Cartão',
  OTHER: 'Outro',
};

export const CANTEEN_SCHEDULE_PERIODS = [
  'MORNING_BREAK',
  'AFTERNOON_BREAK',
  'NIGHT_BREAK',
  'LUNCH',
] as const;

export type CanteenSchedulePeriod = (typeof CANTEEN_SCHEDULE_PERIODS)[number];

export const CANTEEN_SCHEDULE_PERIOD_LABELS: Record<
  CanteenSchedulePeriod,
  string
> = {
  MORNING_BREAK: 'Intervalo da manhã',
  AFTERNOON_BREAK: 'Intervalo da tarde',
  NIGHT_BREAK: 'Intervalo da noite',
  LUNCH: 'Almoço',
};

export const CANTEEN_SCHEDULE_PERIOD_CLOCK: Record<
  CanteenSchedulePeriod,
  string
> = {
  MORNING_BREAK: '09:30',
  AFTERNOON_BREAK: '15:30',
  NIGHT_BREAK: '17:30',
  LUNCH: '12:00',
};

export const CANTEEN_TRANSACTION_TYPES = [
  'CREDIT',
  'DEBIT',
  'REFUND',
  'ADJUSTMENT',
  'TRANSFER',
  'CANCELLATION',
] as const;

export type CanteenTransactionType = (typeof CANTEEN_TRANSACTION_TYPES)[number];

export const CANTEEN_TRANSACTION_LABELS: Record<
  CanteenTransactionType,
  string
> = {
  CREDIT: 'Crédito',
  DEBIT: 'Débito',
  REFUND: 'Devolução',
  ADJUSTMENT: 'Ajuste',
  TRANSFER: 'Transferência',
  CANCELLATION: 'Cancelamento',
};

export const CANTEEN_STOCK_MOVEMENT_TYPES = [
  'ENTRY',
  'SALE',
  'REFUND',
  'LOSS',
  'ADJUSTMENT',
  'INVENTORY',
] as const;

export type CanteenStockMovementType =
  (typeof CANTEEN_STOCK_MOVEMENT_TYPES)[number];

export const CANTEEN_STOCK_MOVEMENT_LABELS: Record<
  CanteenStockMovementType,
  string
> = {
  ENTRY: 'Entrada',
  SALE: 'Venda',
  REFUND: 'Devolução',
  LOSS: 'Perda',
  ADJUSTMENT: 'Ajuste',
  INVENTORY: 'Inventário',
};

export const CANTEEN_CASHIER_STATUSES = ['OPEN', 'CLOSED'] as const;
export type CanteenCashierStatus = (typeof CANTEEN_CASHIER_STATUSES)[number];

export const CANTEEN_CASHIER_MOVEMENT_TYPES = [
  'OPENING',
  'SALE',
  'ENTRY',
  'WITHDRAWAL',
  'REFUND',
  'ADJUSTMENT',
] as const;

export type CanteenCashierMovementType =
  (typeof CANTEEN_CASHIER_MOVEMENT_TYPES)[number];

export const CANTEEN_CASHIER_MOVEMENT_LABELS: Record<
  CanteenCashierMovementType,
  string
> = {
  OPENING: 'Abertura',
  SALE: 'Venda',
  ENTRY: 'Entrada',
  WITHDRAWAL: 'Retirada',
  REFUND: 'Devolução',
  ADJUSTMENT: 'Ajuste',
};

export const CANTEEN_ORDER_CHANNELS = ['APP', 'POS'] as const;
export type CanteenOrderChannel = (typeof CANTEEN_ORDER_CHANNELS)[number];

export interface CanteenResult<T> {
  readonly ok: boolean;
  readonly value: T | null;
  readonly message: string | null;
}

export interface CanteenSchool {
  readonly id: string;
  readonly tenantId: string;
  readonly name: string;
}

export interface CanteenClassGroup {
  readonly id: string;
  readonly schoolId: string;
  readonly name: string;
}

export interface CanteenUnit {
  readonly id: string;
  readonly tenantId: string;
  readonly schoolId: string;
  readonly name: string;
  readonly description: string;
  readonly status: CanteenUnitStatus;
  readonly opensAt: string;
  readonly closesAt: string;
  readonly allowsSale: boolean;
  readonly allowsScheduling: boolean;
  readonly allowsBalance: boolean;
  readonly allowsCounterSale: boolean;
  readonly allowsPickup: boolean;
  readonly allowsStudentOrders: boolean;
  readonly minimumSchedulingAdvanceHours: number;
  readonly cancellationWindowHours: number;
}

export interface CanteenProfile {
  readonly id: string;
  readonly name: string;
  readonly role: CanteenRole;
  readonly guardianId: string | null;
  readonly studentId: string | null;
  /** Foto do perfil (`avatar_url`). Vazio: iniciais no avatar. */
  readonly avatarUrl?: string | null;
}

export interface CanteenGuardian {
  readonly id: string;
  readonly name: string;
  readonly studentIds: readonly string[];
}

export interface CanteenStudent {
  readonly id: string;
  readonly schoolId: string;
  readonly enrollmentId: string;
  readonly classId: string;
  readonly name: string;
  readonly status: CanteenStudentStatus;
  /** Foto do aluno no SGE (`avatar_url`). Vazio: iniciais no avatar. */
  readonly avatarUrl?: string | null;
}

export interface CanteenAccount {
  readonly id: string;
  readonly tenantId: string;
  readonly schoolId: string;
  readonly canteenId: string;
  readonly studentId: string;
  readonly balance: number;
  readonly dailyLimit: number | null;
  readonly weeklyLimit: number | null;
  readonly monthlyLimit: number | null;
  readonly orderLimit: number | null;
  readonly status: CanteenAccountStatus;
}

export interface CanteenTransaction {
  readonly id: string;
  readonly tenantId: string;
  readonly schoolId: string;
  readonly canteenId: string;
  readonly accountId: string;
  readonly studentId: string;
  readonly type: CanteenTransactionType;
  readonly amount: number;
  readonly balanceAfter: number;
  readonly description: string;
  readonly occurredAt: string;
  readonly orderId: string | null;
}

export interface CanteenCategory {
  readonly id: string;
  readonly tenantId: string;
  readonly schoolId: string;
  readonly canteenId: string;
  readonly name: string;
  readonly active: boolean;
}

export interface CanteenProduct {
  readonly id: string;
  readonly tenantId: string;
  readonly schoolId: string;
  readonly canteenId: string;
  readonly categoryId: string;
  readonly name: string;
  readonly description: string;
  readonly price: number;
  readonly cost: number;
  readonly code: string;
  readonly unit: string;
  readonly stockControlled: boolean;
  readonly stock: number;
  readonly minStock: number;
  readonly active: boolean;
  readonly availableForSale: boolean;
  readonly availableForScheduling: boolean;
  readonly ingredients: readonly string[];
  readonly allergens: readonly string[];
  readonly nutrition: string;
  readonly imageUrl: string | null;
}

export interface CanteenMenuSlot {
  readonly id: string;
  readonly tenantId: string;
  readonly schoolId: string;
  readonly canteenId: string;
  readonly productId: string;
  readonly featured: boolean;
  readonly weekdays: readonly CanteenWeekday[];
  readonly startsAt: string;
  readonly endsAt: string;
  readonly validFrom: string;
  readonly validUntil: string | null;
  readonly availableForScheduling: boolean;
  readonly price: number | null;
}

export interface CanteenPromotion {
  readonly id: string;
  readonly tenantId: string;
  readonly schoolId: string;
  readonly canteenId: string;
  readonly name: string;
  readonly kind: 'PERCENT' | 'FIXED' | 'PROMO_PRICE';
  readonly productId: string;
  readonly value: number;
  readonly validFrom: string;
  readonly validUntil: string | null;
  readonly active: boolean;
}

export interface CanteenBlockedProduct {
  readonly studentId: string;
  readonly productId: string;
}

export interface CanteenDietaryRestriction {
  readonly id: string;
  readonly studentId: string;
  readonly ingredient: string;
}

export interface CanteenOrderItem {
  readonly productId: string;
  readonly productName: string;
  readonly quantity: number;
  readonly unitPrice: number;
  readonly total: number;
}

export interface CanteenOrder {
  readonly id: string;
  readonly number: string;
  readonly tenantId: string;
  readonly schoolId: string;
  readonly canteenId: string;
  readonly studentId: string;
  readonly guardianId: string | null;
  readonly items: readonly CanteenOrderItem[];
  readonly subtotal: number;
  readonly discount: number;
  readonly total: number;
  readonly paymentMethod: CanteenPaymentMethod;
  readonly channel: CanteenOrderChannel;
  readonly createdAt: string;
  readonly scheduledFor: string | null;
  readonly status: CanteenOrderStatus;
  readonly qrCode: string;
  readonly pickedUpBy: string | null;
  readonly pickedUpAt: string | null;
  readonly operatorId: string | null;
}

export interface CanteenStockMovement {
  readonly id: string;
  readonly tenantId: string;
  readonly schoolId: string;
  readonly canteenId: string;
  readonly productId: string;
  readonly type: CanteenStockMovementType;
  readonly quantity: number;
  readonly occurredAt: string;
  readonly note: string;
}

export interface CanteenCashierMovement {
  readonly id: string;
  readonly sessionId: string;
  readonly type: CanteenCashierMovementType;
  readonly amount: number;
  readonly occurredAt: string;
  readonly note: string;
}

export interface CanteenCashierSession {
  readonly id: string;
  readonly tenantId: string;
  readonly schoolId: string;
  readonly canteenId: string;
  readonly status: CanteenCashierStatus;
  readonly openedAt: string;
  readonly closedAt: string | null;
  readonly openingBalance: number;
  readonly openedBy: string;
}

export interface CanteenAuditLog {
  readonly id: string;
  readonly tenantId: string;
  readonly schoolId: string;
  readonly userId: string;
  readonly action: string;
  readonly entity: string;
  readonly entityId: string;
  readonly timestamp: string;
  readonly description: string;
}

export interface CanteenNotice {
  readonly id: string;
  readonly title: string;
  readonly body: string;
  readonly createdAt: string;
  readonly role: CanteenRole | 'ALL';
}

export interface PlaceCanteenOrderDto {
  readonly studentId: string;
  readonly lines: readonly CanteenCartLine[];
  readonly paymentMethod: CanteenPaymentMethod;
  readonly scheduledFor: string | null;
  readonly channel: CanteenOrderChannel;
}

export interface CanteenCartLine {
  readonly productId: string;
  readonly quantity: number;
}

export interface SaveCanteenProductDto {
  readonly id?: string;
  readonly categoryId: string;
  readonly name: string;
  readonly description: string;
  readonly price: number;
  readonly cost: number;
  readonly code: string;
  readonly unit: string;
  readonly stockControlled: boolean;
  readonly stock: number;
  readonly minStock: number;
  readonly active: boolean;
  readonly availableForSale: boolean;
  readonly availableForScheduling: boolean;
  readonly ingredients: readonly string[];
  readonly allergens: readonly string[];
  readonly nutrition: string;
  readonly imageUrl: string | null;
}

export interface SaveCanteenLimitsDto {
  readonly dailyLimit: number | null;
  readonly weeklyLimit: number | null;
  readonly monthlyLimit: number | null;
  readonly orderLimit: number | null;
}

export function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

export function formatCanteenCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

export function formatCanteenDateTime(iso: string): string {
  const date = new Date(iso);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

export function toIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function parseIsoDate(iso: string): Date {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1);
}

export function canteenInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.charAt(0) ?? '';
  const last =
    parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : '';
  const initials = `${first}${last}`.toUpperCase();
  return initials || '?';
}

export function signedTransactionAmount(
  type: CanteenTransactionType,
  amount: number,
): number {
  if (type === 'DEBIT') {
    return -Math.abs(amount);
  }
  return Math.abs(amount);
}

export function okResult<T>(value: T): CanteenResult<T> {
  return { ok: true, value, message: null };
}

export function failResult<T>(message: string): CanteenResult<T> {
  return { ok: false, value: null, message };
}
